<template>
  <div class="admin-leads-index p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">All Leads</h1>
        <p class="text-gray-600 mt-1">Manage and view all leads from all users</p>
      </div>
      <div class="flex items-center gap-4">
        <div class="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
          <span class="text-sm text-gray-600">Total Leads</span>
          <p class="text-2xl font-bold text-green-700">{{ totalLeads }}</p>
        </div>
        <a
          :href="route('admin.leads.export')"
          class="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
        >
          <icon name="download" class="w-5 h-5" />
          Export CSV
        </a>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-6">
      <form @submit.prevent="applyFilters" class="flex flex-col sm:flex-row gap-4">
        <div class="flex-1">
          <input
            v-model="filters.search"
            type="text"
            placeholder="Search by name or email..."
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div class="flex gap-2">
          <input
            v-model="filters.date_from"
            type="date"
            placeholder="From Date"
            class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
          <input
            v-model="filters.date_to"
            type="date"
            placeholder="To Date"
            class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <button
          type="submit"
          class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Filter
        </button>
        <button
          type="button"
          @click="clearFilters"
          class="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Clear
        </button>
      </form>
    </div>

    <!-- Leads Table -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bio Page</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="lead in leads.data" :key="lead.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ lead.created_at }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ lead.name || 'N/A' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <a :href="'mailto:' + lead.email" class="text-green-600 hover:text-green-800 hover:underline">
                  {{ lead.email }}
                </a>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600 max-w-md">
                <div class="truncate" :title="lead.message || 'No message'">
                  {{ lead.message || '—' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <a
                  v-if="lead.profile"
                  :href="route('linkinbio.view', lead.profile.slug)"
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                >
                  {{ lead.profile.name }}
                  <icon name="arrow-right" class="w-4 h-4" />
                </a>
                <span v-else class="text-gray-400">N/A</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <span v-if="lead.user">{{ lead.user.name }}</span>
                <span v-else class="text-gray-400">N/A</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="flex items-center gap-2">
                  <Link
                    :href="route('admin.leads.show', lead.id)"
                    class="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View
                  </Link>
                  <button
                    @click="deleteLead(lead.id)"
                    class="text-red-600 hover:text-red-800 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="leads.data.length === 0">
              <td colspan="7" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center gap-3">
                  <icon name="contact" class="w-16 h-16 text-gray-300" />
                  <p class="text-gray-500 text-lg font-medium">No leads found</p>
                  <p class="text-gray-400 text-sm">Try adjusting your filters</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="leads.links && leads.links.length > 3" class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ leads.from }} to {{ leads.to }} of {{ leads.total }} results
        </div>
        <div class="flex gap-2">
          <Link
            v-for="link in leads.links"
            :key="link.label"
            :href="link.url || '#'"
            v-html="link.label"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              link.active
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300',
              !link.url ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    leads: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    totalLeads: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: 'All Leads',
    },
  },
  data() {
    return {
      localFilters: {
        search: this.filters.search || '',
        date_from: this.filters.date_from || '',
        date_to: this.filters.date_to || '',
      },
    }
  },
  methods: {
    applyFilters() {
      router.get(route('admin.leads.index'), this.localFilters, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    clearFilters() {
      this.localFilters = {
        search: '',
        date_from: '',
        date_to: '',
      }
      router.get(route('admin.leads.index'), {}, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    deleteLead(id) {
      if (confirm('Are you sure you want to delete this lead?')) {
        router.delete(route('admin.leads.destroy', id), {
          preserveScroll: true,
          onSuccess: () => {
            if (window.$toast) {
              window.$toast.success('Lead deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-leads-index {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



